import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  MessageCircle,
  Mail,
  Trash2,
  Eye,
  EyeOff,
  TrendingUp,
  Users,
  Clock,
  Download,
  MapPin,
  Lightbulb,
  StickyNote,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type FollowUpStatus = "new" | "reviewed" | "contacted" | "closed";

interface FeedbackRow {
  id: string;
  created_at: string;
  helpful_rating: string | null;
  pathway_found: string | null;
  user_goal: string | null;
  missing_pathway: string | null;
  state: string | null;
  feedback_text: string | null;
  testimonial_permission: boolean;
  opted_for_followup: boolean;
  name: string | null;
  contact: string | null;
  email: string | null;
  whatsapp: string | null;
  admin_notes: string | null;
  interest_category: string | null;
  education_level: string | null;
  selected_roadmap: string | null;
  session_id: string | null;
  time_spent_before_feedback_ms: number | null;
  is_published: boolean;
  follow_up_status: FollowUpStatus;
}

interface AnalyticsRow {
  id: string;
  interest_category: string | null;
  event_type: string;
}

export const FeedbackPanel = () => {
  const queryClient = useQueryClient();
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  const { data: feedback = [], isLoading } = useQuery<FeedbackRow[]>({
    queryKey: ["admin-feedback"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("feedback_leads")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data as FeedbackRow[];
    },
  });

  const { data: skillstemClicks = [] } = useQuery<AnalyticsRow[]>({
    queryKey: ["admin-skillstem-clicks"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("analytics_events")
        .select("id, interest_category, event_type")
        .eq("event_type", "skillstem_cta_click")
        .limit(1000);
      if (error) throw error;
      return data as AnalyticsRow[];
    },
  });

  const publishMutation = useMutation({
    mutationFn: async ({ id, publish }: { id: string; publish: boolean }) => {
      const { error } = await (supabase as any)
        .from("feedback_leads")
        .update({
          is_published: publish,
          published_at: publish ? new Date().toISOString() : null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { publish }) => {
      toast.success(publish ? "Published as testimonial." : "Unpublished.");
      queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: () => toast.error("Update failed."),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from("feedback_leads").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Deleted.");
      queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: () => toast.error("Delete failed."),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: FollowUpStatus }) => {
      const { error } = await (supabase as any)
        .from("feedback_leads")
        .update({ follow_up_status: status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Status updated.");
      queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: () => toast.error("Could not update status."),
  });

  const notesMutation = useMutation({
    mutationFn: async ({ id, admin_notes }: { id: string; admin_notes: string }) => {
      const { error } = await (supabase as any)
        .from("feedback_leads")
        .update({ admin_notes: admin_notes || null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Notes saved.");
      queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: () => toast.error("Could not save notes."),
  });

  const exportFollowUpLeadsCsv = () => {
    const leads = feedback.filter((f) => f.opted_for_followup);
    if (leads.length === 0) {
      toast.info("No follow-up leads to export.");
      return;
    }
    const headers = [
      "created_at",
      "follow_up_status",
      "name",
      "email",
      "whatsapp",
      "contact",
      "helpful_rating",
      "pathway_found",
      "user_goal",
      "state",
      "interest_category",
      "education_level",
      "selected_roadmap",
      "feedback_text",
      "missing_pathway",
      "admin_notes",
    ];
    const escape = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = leads.map((f) =>
      [
        f.created_at,
        f.follow_up_status ?? "new",
        f.name ?? "",
        f.email ?? "",
        f.whatsapp ?? "",
        f.contact ?? "",
        f.helpful_rating ?? "",
        f.pathway_found ?? "",
        f.user_goal ?? "",
        f.state ?? "",
        f.interest_category ?? "",
        f.education_level ?? "",
        f.selected_roadmap ?? "",
        f.feedback_text ?? "",
        f.missing_pathway ?? "",
        f.admin_notes ?? "",
      ]
        .map(escape)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wehelp-followup-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${leads.length} lead${leads.length === 1 ? "" : "s"}.`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const total = feedback.length;
  const helpful = feedback.filter((f) => f.helpful_rating === "helpful").length;
  const needsImprovement = feedback.filter((f) => f.helpful_rating === "needs_improvement").length;
  const rated = helpful + needsImprovement;
  const positivePct = rated > 0 ? Math.round((helpful / rated) * 100) : 0;
  const followUpCount = feedback.filter((f) => f.opted_for_followup).length;
  const followUpPct = total > 0 ? Math.round((followUpCount / total) * 100) : 0;
  const newCount = feedback.filter((f) => f.opted_for_followup && (f.follow_up_status ?? "new") === "new").length;
  const avgTimeMs =
    feedback.reduce((s, f) => s + (f.time_spent_before_feedback_ms ?? 0), 0) / (total || 1);

  const interestCounts: Record<string, number> = {};
  feedback.forEach((f) => {
    if (f.interest_category) {
      interestCounts[f.interest_category] = (interestCounts[f.interest_category] ?? 0) + 1;
    }
  });
  const topInterests = Object.entries(interestCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const stateCounts: Record<string, number> = {};
  feedback.forEach((f) => {
    if (f.state) stateCounts[f.state] = (stateCounts[f.state] ?? 0) + 1;
  });
  const topStates = Object.entries(stateCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const goalCounts: Record<string, number> = {};
  feedback.forEach((f) => {
    if (f.user_goal) goalCounts[f.user_goal] = (goalCounts[f.user_goal] ?? 0) + 1;
  });
  const topGoals = Object.entries(goalCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const missingPathways = feedback
    .filter((f) => f.missing_pathway && f.missing_pathway.trim())
    .slice(0, 10);

  const skillstemCounts: Record<string, number> = {};
  skillstemClicks.forEach((c) => {
    const k = c.interest_category ?? "unknown";
    skillstemCounts[k] = (skillstemCounts[k] ?? 0) + 1;
  });
  const topSkillstem = Object.entries(skillstemCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<MessageCircle className="h-4 w-4" />} label="Total Feedback" value={total} />
        <StatCard icon={<TrendingUp className="h-4 w-4" />} label="Positive %" value={`${positivePct}%`} />
        <StatCard icon={<Users className="h-4 w-4" />} label="Follow-up Requests" value={`${followUpCount} (${followUpPct}%)`} />
        <StatCard icon={<Clock className="h-4 w-4" />} label="Avg time before submit" value={`${Math.round(avgTimeMs / 1000)}s`} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Top Interest Categories
            </h3>
            {topInterests.length === 0 ? (
              <p className="text-sm text-muted-foreground">No data yet.</p>
            ) : (
              <ul className="space-y-2">
                {topInterests.map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-sm">
                    <span className="font-medium capitalize">{k}</span>
                    <Badge variant="outline">{v}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> Top States
            </h3>
            {topStates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No state data yet.</p>
            ) : (
              <ul className="space-y-2">
                {topStates.map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{k}</span>
                    <Badge variant="outline">{v}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
              What Students Were Looking For
            </h3>
            {topGoals.length === 0 ? (
              <p className="text-sm text-muted-foreground">No data yet.</p>
            ) : (
              <ul className="space-y-2">
                {topGoals.map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-sm">
                    <span className="font-medium capitalize">{k.replace(/_/g, " ")}</span>
                    <Badge variant="outline">{v}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
              <Lightbulb className="h-3.5 w-3.5" /> Most Requested Missing Pathways
            </h3>
            {missingPathways.length === 0 ? (
              <p className="text-sm text-muted-foreground">No requests yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {missingPathways.map((f) => (
                  <li key={f.id} className="text-foreground border-l-2 border-primary/40 pl-2">
                    {f.missing_pathway}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Most Clicked SkillSTEM Categories
            </h3>
            {topSkillstem.length === 0 ? (
              <p className="text-sm text-muted-foreground">No clicks yet.</p>
            ) : (
              <ul className="space-y-2">
                {topSkillstem.map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-sm">
                    <span className="font-medium capitalize">{k}</span>
                    <Badge variant="outline">{v}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent comments */}
      <div>
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="text-lg font-bold text-foreground">
            Recent Feedback
            {newCount > 0 && (
              <Badge className="ml-2 bg-blue-100 text-blue-800 border-blue-200">
                {newCount} new follow-up
              </Badge>
            )}
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={exportFollowUpLeadsCsv}
            className="gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            Export follow-up leads (CSV)
          </Button>
        </div>
        {feedback.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>No feedback yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {feedback.map((f) => (
              <div key={f.id} className="p-4 rounded-xl bg-card border border-border">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {f.helpful_rating === "helpful" && (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
                          <ThumbsUp className="h-3 w-3" /> Helpful
                        </Badge>
                      )}
                      {f.helpful_rating === "needs_improvement" && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1">
                          <ThumbsDown className="h-3 w-3" /> Needs Improvement
                        </Badge>
                      )}
                      {f.helpful_rating === "not_sure" && (
                        <Badge className="bg-slate-100 text-slate-800 border-slate-200 gap-1">
                          <HelpCircle className="h-3 w-3" /> Not Sure
                        </Badge>
                      )}
                      {f.pathway_found && (
                        <Badge variant="outline" className="capitalize">
                          Pathway: {f.pathway_found.replace(/_/g, " ")}
                        </Badge>
                      )}
                      {f.user_goal && (
                        <Badge variant="outline" className="capitalize">
                          {f.user_goal.replace(/_/g, " ")}
                        </Badge>
                      )}
                      {f.state && (
                        <Badge variant="outline" className="gap-1">
                          <MapPin className="h-3 w-3" />
                          {f.state}
                        </Badge>
                      )}
                      {f.interest_category && <Badge variant="outline">{f.interest_category}</Badge>}
                      {f.education_level && <Badge variant="outline">{f.education_level}</Badge>}
                      {f.opted_for_followup && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 gap-1">
                          <Mail className="h-3 w-3" /> Follow-up
                        </Badge>
                      )}
                      {f.testimonial_permission && (
                        <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                          Testimonial OK
                        </Badge>
                      )}
                      {f.is_published && (
                        <Badge className="bg-primary text-primary-foreground">Published</Badge>
                      )}
                      {f.opted_for_followup && (
                        <Badge
                          variant="outline"
                          className={
                            (f.follow_up_status ?? "new") === "new"
                              ? "border-blue-300 text-blue-700"
                              : (f.follow_up_status ?? "new") === "reviewed"
                              ? "border-purple-300 text-purple-700"
                              : (f.follow_up_status ?? "new") === "contacted"
                              ? "border-amber-300 text-amber-700"
                              : "border-emerald-300 text-emerald-700"
                          }
                        >
                          {(f.follow_up_status ?? "new").toUpperCase()}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(f.created_at).toLocaleString("en-IN", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    {f.feedback_text && (
                      <p className="text-sm text-foreground whitespace-pre-wrap bg-muted/40 rounded-lg p-3 mb-2">
                        {f.feedback_text}
                      </p>
                    )}
                    {f.missing_pathway && (
                      <p className="text-xs text-foreground bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                        <span className="font-bold">Missing pathway:</span> {f.missing_pathway}
                      </p>
                    )}
                    {(f.name || f.email || f.whatsapp || f.contact) && (
                      <p className="text-xs text-muted-foreground">
                        {f.name && <span className="font-medium text-foreground">{f.name}</span>}
                        {f.name && (f.email || f.whatsapp || f.contact) && " · "}
                        {[f.email, f.whatsapp, f.contact].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {f.opted_for_followup && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
                          Follow-up
                        </span>
                        <Select
                          value={f.follow_up_status ?? "new"}
                          onValueChange={(v) =>
                            statusMutation.mutate({ id: f.id, status: v as FollowUpStatus })
                          }
                        >
                          <SelectTrigger className="h-8 w-[150px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="reviewed">Reviewed</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Admin notes */}
                    <div className="mt-3">
                      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1 mb-1">
                        <StickyNote className="h-3 w-3" /> Admin notes (private)
                      </Label>
                      <Textarea
                        rows={2}
                        value={noteDrafts[f.id] ?? f.admin_notes ?? ""}
                        onChange={(e) =>
                          setNoteDrafts((prev) => ({ ...prev, [f.id]: e.target.value }))
                        }
                        placeholder="Internal notes — never shown publicly"
                        className="text-xs"
                      />
                      <div className="flex justify-end mt-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={
                            notesMutation.isPending ||
                            (noteDrafts[f.id] ?? f.admin_notes ?? "") === (f.admin_notes ?? "")
                          }
                          onClick={() =>
                            notesMutation.mutate({
                              id: f.id,
                              admin_notes: (noteDrafts[f.id] ?? "").trim(),
                            })
                          }
                          className="h-7 text-xs"
                        >
                          Save note
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex sm:flex-col gap-2 shrink-0">
                    {f.testimonial_permission && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          publishMutation.mutate({ id: f.id, publish: !f.is_published })
                        }
                        disabled={publishMutation.isPending}
                        className="gap-1.5"
                      >
                        {f.is_published ? (
                          <>
                            <EyeOff className="h-3.5 w-3.5" /> Unpublish
                          </>
                        ) : (
                          <>
                            <Eye className="h-3.5 w-3.5" /> Publish
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (confirm("Delete this feedback?")) deleteMutation.mutate(f.id);
                      }}
                      disabled={deleteMutation.isPending}
                      className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider font-bold mb-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-foreground">{value}</div>
    </CardContent>
  </Card>
);